/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
/* eslint-disable max-len */

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { animated, useResize, useSpring } from '@react-spring/web';
import { cva } from 'class-variance-authority';
import {
    ArrowUpCircle,
    Blocks,
    ChevronRight,
    Circle,
    Clock,
    CommandIcon,
    Copy,
    CornerDownLeft,
    CurlyBraces,
    DramaIcon,
    GripVertical,
    Link2,
    List,
    LucideProps,
    MailCheck,
    Maximize2,
    Microscope,
    Minimize2,
    MousePointer2,
    Play,
    Plus,
    Search,
    TextCursorInput,
    Trash,
} from 'lucide-react';
import React, { Dispatch, FC, ForwardRefExoticComponent, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { HighlightVariables } from './HighlightVariables';
import useLayerGenerator from './hooks/useLayerGenerator';
import { getStepParams, isStepExecutable, ParamsMap } from './LiveAgentSessionUtils';
import { AtomicActionForm } from './LiveAgentStepActionForm';
import { StepContextualMenu } from './LiveAgentStepContextualMenu';
import { LiveAgentStepHeader } from './LiveAgentStepHeader';
import { LiveAgentSteps } from './LiveAgentSteps';
import { actionProps, Step } from './LiveAgentTypes';
import StepErrorBoundary from './StepErrorBoundary';
import { TargetingStrategyBadge } from './TargetingStrategyBadge';

import { Button } from '@/components/atoms/Button';
import { cn } from '@/lib/utils';

export const actionToIcon: Record<keyof typeof actionProps, ForwardRefExoticComponent<Omit<LucideProps, 'ref'>>> = {
    leftClick: MousePointer2,
    typeText: TextCursorInput,
    pressKey: CommandIcon,
    pressEnter: CornerDownLeft,
    selectOption: List,
    hover: ArrowUpCircle,
    gotoUrl: Link2,
    emailSignup: MailCheck,
    emailLogin: MailCheck,
    readSMS: MailCheck,
    recordedActions: Circle,
    simpleAssertion: Microscope,
    aiAssertion: Microscope,
    wait: Clock,
    block: Blocks,
    execPlaywright: DramaIcon,
    aiExtractInfo: CurlyBraces,
    extractSelector: Search,
};

export const LiveAgentStepComponent: FC<{
    step: Step;
    index: number;
    parentIndex?: number;
    dispatch?: Dispatch<any>;
    executeStep?: (step: Step) => void;
    removeStep?: (step: Step) => void;
    highlightSelectors?: (
        selectors: { [key: string]: { selector: string; unique: boolean } } | null,
        force?: boolean,
    ) => void;
    isDisabled: boolean;
    queued?: boolean;
    queue?: number[];
    setQueue?: (index: number) => void;
    resetQueue?: () => void;
    inert?: boolean;
    variables: { [key: string]: string };
    precomputedVariables?: { [key: string]: string };
    editVariables?: (action: 'add' | 'remove', payload: { [key: string]: string }) => void;
    openStep?: string | null;
    updateOpenStep?: (stepId: string, force?: boolean) => void;
    inLayer?: boolean;
    isFullScreen?: boolean;
    closeLayer?: () => void;
    fullScreenContainer?: React.RefObject<HTMLDivElement>;
    hidePlaywright?: boolean;
}> = (props) => {
    const {
        step,
        index,
        parentIndex,
        dispatch,
        executeStep,
        removeStep,
        highlightSelectors,
        isDisabled,
        queued,
        queue,
        setQueue,
        resetQueue,
        inert,
        variables,
        precomputedVariables = {},
        editVariables,
        openStep = 'all',
        updateOpenStep = () => {},
        inLayer = false,
        isFullScreen = false,
        closeLayer = () => {},
        fullScreenContainer,
        hidePlaywright = false,
    } = props;

    const { setNodeRef, attributes, listeners, isDragging, transform, transition } = useSortable({
        id: step.id,
        data: {
            index,
            parentIndex,
        },
    });

    const isFullScreenable = ['execPlaywright'].includes(step.kind);

    const {
        openLayer,
        layer,
        isOpen: isLayerOpen,
    } = useLayerGenerator(
        {
            content: inLayer || !isFullScreenable ? null : <LiveAgentStepComponent {...props} inLayer isFullScreen />,
        },
        fullScreenContainer,
    );

    const style = {
        transform: CSS.Translate.toString(transform),
        transition,
    };

    const variants = cva('', {
        variants: {
            dragging: {
                over: 'opacity-40 relative z-10',
                under: 'opacity-10',
            },
        },
    });

    let header;
    let content;

    let isCollapsed = queued && isDisabled;
    if (step.id !== openStep && openStep !== 'all') {
        isCollapsed = true;
    }
    if (isFullScreen) {
        isCollapsed = false;
    }

    switch (step.kind) {
        case 'separator':
            break;
        case 'aiAtomic':
        case 'gotoUrl':
        case 'wait':
        case 'emailSignup':
        case 'emailLogin':
        case 'readSMS':
        case 'simpleAssertion':
        case 'aiAssertion':
        case 'execPlaywright':
        case 'aiExtractInfo':
        case 'pressKey':
            header = (() => {
                const stepType = step.action || step.kind;

                const Icon = actionToIcon[stepType as keyof typeof actionToIcon];
                const action = actionProps[stepType as keyof typeof actionToIcon];

                const stepParams = getStepParams(step, action.paramsMap as ParamsMap);

                return (
                    <LiveAgentStepHeader
                        step={step}
                        icon={<Icon />}
                        title={action.label}
                        variables={variables}
                        precomputedVariables={precomputedVariables}
                        hidePlaywright={hidePlaywright}
                        inert={inert}
                        parentIndex={parentIndex}
                        subtitle={
                            (step.executed || isCollapsed) && (
                                <>
                                    <TargetingStrategyBadge
                                        targetingStrategy={step.targetingStrategy}
                                        className="me-1"
                                    />
                                    {stepParams.value && (
                                        <>
                                            {/* eslint-disable-next-line max-len */}
                                            <span className="border p-1 rounded-md">
                                                <HighlightVariables
                                                    text={stepParams.value}
                                                    variables={variables}
                                                    precomputedVariables={precomputedVariables}
                                                />
                                            </span>{' '}
                                            on{' '}
                                        </>
                                    )}
                                    <span
                                        onClick={
                                            highlightSelectors && step.actions
                                                ? () => {
                                                      highlightSelectors(step.actions?.[0]?.selectors || {});
                                                  }
                                                : () => {}
                                        }
                                    >
                                        <HighlightVariables
                                            text={
                                                stepType !== 'wait'
                                                    ? stepParams.target
                                                    : `Waited for ${Number(stepParams.target) / 1000} second${Number(stepParams.target) > 1000 ? 's' : ''}`
                                            }
                                            variables={variables}
                                            precomputedVariables={precomputedVariables}
                                        />
                                    </span>
                                </>
                            )
                        }
                        isCollapsed={isCollapsed}
                        updateOpenStep={updateOpenStep}
                    />
                );
            })();
            content = (
                <>
                    {!step.executed && dispatch && executeStep && !isCollapsed && (
                        <AtomicActionForm
                            step={step}
                            executeStep={executeStep}
                            isDisabled={isDisabled}
                            index={index}
                            parentIndex={parentIndex}
                            dispatch={dispatch}
                            setQueue={setQueue}
                            resetQueue={resetQueue}
                            highlightSelectors={highlightSelectors}
                            variables={variables}
                            editVariables={editVariables}
                            isFullScreen={isFullScreen}
                            isLayerOpen={isLayerOpen}
                            closeLayer={isFullScreenable ? closeLayer : () => {}}
                        >
                            {/* only show sub actions if there are more than one */}
                            {!['emailLogin', 'emailSignup', 'readSMS'].includes(step.kind) &&
                                step.actions &&
                                step.actions.length > 1 && (
                                    <ul className="ps-9">
                                        {step.actions.map((action, i) => {
                                            const Icon = actionToIcon[action.name as keyof typeof actionToIcon];
                                            return (
                                                <li
                                                    key={i}
                                                    className="flex items-center gap-2 py-1 whitespace-nowrap text-sm"
                                                >
                                                    <Icon
                                                        className="text-slate-500 shrink-0"
                                                        width="16px"
                                                        height="16px"
                                                    />{' '}
                                                    <span className="truncate py-1">
                                                        {action.name}{' '}
                                                        {action.value && (
                                                            <span className="border p-1 rounded-md">
                                                                {action.value}
                                                            </span>
                                                        )}
                                                        <span className="text-slate-500">
                                                            {action.targetDescription
                                                                ? ` on ${action.targetDescription}`
                                                                : ''}
                                                        </span>
                                                    </span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                        </AtomicActionForm>
                    )}
                </>
            );
            break;
        case 'aiPrompt':
            header = 'Prompt';
            content = 'Prompt';
            break;
        case 'recordedActions':
            header = (
                <LiveAgentStepHeader
                    step={step}
                    icon={<Circle />}
                    variables={variables}
                    precomputedVariables={precomputedVariables}
                    title="Recorded actions"
                    subtitle={step.intent && <span>{step.intent}</span>}
                    isCollapsed={isCollapsed}
                    updateOpenStep={updateOpenStep}
                    inert={inert}
                    parentIndex={parentIndex}
                />
            );
            content = (
                <>
                    <ul className="ps-9">
                        {(step.actions || step.recorded || []).map((action, i) => {
                            const Icon = actionToIcon[action.name as keyof typeof actionToIcon];
                            const actionName = actionProps[action.name as keyof typeof actionProps].label;
                            return (
                                <li
                                    key={i}
                                    className="flex items-center gap-2 py-1 whitespace-nowrap text-sm"
                                    onClick={
                                        highlightSelectors
                                            ? () => {
                                                  highlightSelectors(action.selectors);
                                              }
                                            : () => {}
                                    }
                                >
                                    {/* refactor with atomic one as ActionComponent */}
                                    <Icon className="text-slate-500 shrink-0" width="16px" height="16px" />{' '}
                                    <span className="truncate py-1">
                                        {actionName}{' '}
                                        {action.value && <span className="border p-1 rounded-md">{action.value}</span>}
                                        <span className="text-slate-500">
                                            {action.targetDescription ? ` on ${action.targetDescription}` : ''}
                                        </span>
                                    </span>
                                    {dispatch && (
                                        <>
                                            <button
                                                className="ms-auto"
                                                onClick={() => {
                                                    dispatch({
                                                        type: 'removeAction',
                                                        payload: { index, actionIndex: i },
                                                    });
                                                }}
                                                aria-label="Remove this action"
                                            >
                                                <Trash className="text-slate-500" width="16px" height="16px" />
                                            </button>
                                        </>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                    {!step.executed && executeStep && (
                        <Button
                            type="button"
                            disabled={isDisabled}
                            onMouseEnter={() => setQueue && setQueue(index)}
                            onMouseLeave={resetQueue}
                            onClick={() => {
                                resetQueue && resetQueue();
                                executeStep(step);
                            }}
                            className="mt-1 ms-auto block"
                            color="white"
                            size="small"
                        >
                            <span className="flex items-center gap-x-1">
                                <Play className="text-purple-400" size="16px" />
                                Run
                            </span>
                        </Button>
                    )}
                </>
            );
            break;
        case 'block':
            header = (
                <LiveAgentStepHeader
                    step={step}
                    icon={<Blocks />}
                    title="Block"
                    variables={variables}
                    precomputedVariables={precomputedVariables}
                    subtitle={step.name && <span>{step.name}</span>}
                    isCollapsed={isCollapsed}
                    inert={inert}
                    parentIndex={parentIndex}
                />
            );
            content = (
                <div className="ps-4 pb-1 pe-1">
                    {step.steps && step.steps.length > 0 && (
                        <LiveAgentSteps
                            steps={step.steps}
                            dispatch={dispatch}
                            executeStep={executeStep}
                            removeStep={removeStep}
                            highlightSelectors={highlightSelectors}
                            parentIndex={index}
                            isDisabled={isDisabled}
                            // TODO: inert={inert} to reenable when block steps are fixed
                            inert
                            queue={queue}
                            setQueue={setQueue}
                            resetQueue={resetQueue}
                            variables={variables}
                            precomputedVariables={precomputedVariables}
                            editVariables={editVariables}
                            openStep={openStep}
                            updateOpenStep={updateOpenStep}
                            fullScreenContainer={fullScreenContainer}
                            hidePlaywright={hidePlaywright}
                        />
                    )}
                </div>
            );
            break;
        case 'empty':
            header = '';
            content = (
                <div className="text-center text-slate-400 text-sm pb-6">Select a step type in the command palette</div>
            );
            break;
        default:
            // exhaustive check, so TS type checker warns us if we forget to support a step kind
            step.kind satisfies never;
            header = 'Unknown';
            content = 'Unknown';
            break;
    }

    const isFoldable = step.kind === 'block' || step.kind === 'recordedActions';

    const [isFolded, setIsFolded] = useState(false);

    const spring = useSpring({
        from: { rotate: '-90deg' },
        to: { rotate: isFolded || isDragging ? '-90deg' : '90deg' },
    });

    const mainStepBody = useRef<HTMLDivElement>(null);

    const { height: mainStepBodyHeight } = useResize({ container: mainStepBody });

    const containerStyle = {
        height: mainStepBodyHeight,
        ...(inert ? {} : style),
    };

    return (
        <StepErrorBoundary removeStep={removeStep ? () => removeStep(step) : undefined}>
            <animated.div
                key={step.id}
                style={containerStyle}
                className={cn(
                    'relative flex items-start w-full',
                    step.kind === 'empty' && 'border-slate-300 rounded-lg border-dashed border-2',
                    (step.executed || isCollapsed) && 'LiveAgentStep--executed min-h-fit',
                )}
                ref={setNodeRef}
            >
                <animated.div
                    className={cn(
                        'rounded text-slate-700 w-full',
                        !step.executed && !isCollapsed && step.kind !== 'empty' && 'border bg-slate-100',
                        step.running && 'glassrainbow',
                        variants({ dragging: isDragging && !inert ? 'over' : undefined }),
                    )}
                    ref={mainStepBody}
                >
                    <div
                        className={cn(
                            'rounded px-1 py-2 transition-all duration-200',
                            queued && 'bg-purple-100 step-highlighted',
                        )}
                    >
                        <div className="flex items-center gap-1 py-1 pe-1 whitespace-nowrap">
                            {!inert && (
                                <button {...attributes} onClick={() => {}} {...listeners}>
                                    <span className="sr-only">Move step</span>
                                    <GripVertical className="cursor-move stroke-1 text-slate-400" />
                                </button>
                            )}
                            {header}
                            <span className="ms-auto flex items-center gap-x-2">
                                {isFoldable && (
                                    <button
                                        onClick={() => {
                                            setIsFolded(!isFolded);
                                        }}
                                        aria-label="Vertically collapse this step"
                                    >
                                        <animated.div className="ms-auto" style={{ ...spring }}>
                                            <ChevronRight size="16px" />
                                        </animated.div>
                                    </button>
                                )}

                                {step.executed || isCollapsed ? (
                                    <>
                                        {dispatch && parentIndex === undefined && (
                                            <StepContextualMenu
                                                index={index}
                                                parentIndex={parentIndex}
                                                step={step}
                                                dispatch={dispatch}
                                                removeStep={removeStep}
                                                updateOpenStep={updateOpenStep}
                                            />
                                        )}
                                        {!step.executed &&
                                            !inert &&
                                            parentIndex === undefined &&
                                            openStep !== 'all' &&
                                            executeStep && (
                                                <Button
                                                    onClick={() => {
                                                        resetQueue && resetQueue();
                                                        executeStep(step);
                                                    }}
                                                    disabled={isDisabled || !isStepExecutable(step)}
                                                    title={
                                                        isDisabled
                                                            ? 'The step is disabled'
                                                            : !isStepExecutable(step)
                                                              ? 'The step is not executable'
                                                              : undefined
                                                    }
                                                    onMouseEnter={() => setQueue && setQueue(index)}
                                                    onMouseLeave={resetQueue}
                                                    className="ms-auto block"
                                                    color="white"
                                                    size="small"
                                                >
                                                    <span className="flex items-center gap-x-1">
                                                        <Play className="text-purple-400" size="16px" />
                                                        Run
                                                    </span>
                                                </Button>
                                            )}
                                    </>
                                ) : (
                                    <>
                                        {isFullScreenable && !inert && (
                                            <button
                                                aria-label="Make the step full-screen"
                                                title="Make the step full-screen"
                                                type="button"
                                                onClick={() => {
                                                    if (isFullScreen) {
                                                        closeLayer();
                                                    } else {
                                                        openLayer(
                                                            mainStepBody.current &&
                                                                mainStepBody.current.getBoundingClientRect(),
                                                            0,
                                                        );
                                                    }
                                                }}
                                            >
                                                {isFullScreen ? (
                                                    <Minimize2 className="text-slate-500" width="16px" height="16px" />
                                                ) : (
                                                    <Maximize2 className="text-slate-500" width="16px" height="16px" />
                                                )}
                                            </button>
                                        )}
                                        {dispatch && parentIndex === undefined && (
                                            <button
                                                aria-label={
                                                    step.kind !== 'block'
                                                        ? 'Duplicate this step'
                                                        : 'Duplicate this block'
                                                }
                                                title={
                                                    step.kind !== 'block'
                                                        ? 'Duplicate this step'
                                                        : 'Duplicate this block'
                                                }
                                                type="button"
                                                onClick={() => {
                                                    dispatch({
                                                        type: 'duplicate',
                                                        payload: { index },
                                                    });
                                                }}
                                            >
                                                <Copy className="text-slate-500" width="16px" height="16px" />
                                            </button>
                                        )}
                                        {dispatch && removeStep && parentIndex === undefined && (
                                            <button
                                                aria-label={
                                                    step.kind !== 'block' ? 'Remove this step' : 'Remove this block'
                                                }
                                                title={step.kind !== 'block' ? 'Remove this step' : 'Remove this block'}
                                                type="button"
                                                onClick={() => removeStep(step)}
                                            >
                                                <Trash className="text-slate-500" width="16px" height="16px" />
                                            </button>
                                        )}
                                    </>
                                )}
                            </span>
                        </div>
                        {isFoldable ? (
                            <div className={cn('expandToAutoHeight', isFolded || isDragging ? 'collapsed' : '')}>
                                <div className="min-h-0 overflow-hidden">{content}</div>
                            </div>
                        ) : (
                            content
                        )}
                    </div>
                </animated.div>
                {!inert && parentIndex === undefined && step.kind !== 'empty' && (
                    <div className="flex items-center gap-2 whitespace-nowrap text-slate-400 px-1 py-1 z-[1] opacity-0 hover:opacity-100 transition-opacity ease-in-out duration-300 absolute bottom-0 inset-x-0 translate-y-[50%]">
                        <button
                            className="mx-auto"
                            onClick={() => {
                                dispatch &&
                                    dispatch({
                                        type: 'insertEmptyStep',
                                        payload: { index },
                                    });
                            }}
                            style={{ clipPath: 'circle(50%)' }}
                        >
                            <div className="cursor-pointer bg-white rounded-full border w-[24px] h-[24px] text-purple-700 flex items-center justify-center">
                                <Plus size="16" className="stroke-3" />
                            </div>
                        </button>
                    </div>
                )}
            </animated.div>
            {!inLayer && createPortal(layer, document.getElementById('portal') ?? document.body)}
        </StepErrorBoundary>
    );
};
